// src/data/courses.js
// Source: LISD MS Course Catalog 2025-2026 (PDF)
// https://www.leanderisd.org/wp-content/uploads/2024/09/Leander-ISD-MS-Course-Catalog-2025-2026.pdf
//
// The 2026-27 catalog had not been published as of May 2026. The 25-26 catalog
// is the authoritative source available; courses very rarely change year-over-year
// in this district. The source entry has verificationNeeded: true.
//
// Modeling notes:
// - `grade` is a single number for filter logic. For courses spanning multiple grades
//   (e.g., Art MS 1 grades 6-8), the lowest grade where the course is available is used,
//   and the `notes` field captures the wider availability.
// - Semester / year-long variants of the same course are collapsed to one card.
// - Choir voice-part assignment and band instrument selection are placement decisions,
//   not course choices, so internal sub-codes are collapsed.
// - HS-credit courses get hsCredit: true.

import { SOURCES } from './sources.js';

export const courses = [
  // =========================================================================
  // ENGLISH LANGUAGE ARTS
  // =========================================================================
  {
    id: 'ela-6',
    name: 'English Language Arts 6',
    code: '1601',
    grade: 6,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Sixth grade ELA refines and masters previously learned skills in increasingly complex situations. Students read fiction, non-fiction, poetry, and drama; deepen reading comprehension through literary and informational analysis, vocabulary study, and class discussions; and select appropriate forms of writing for specific audiences and purposes.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'ela-6-advanced',
    name: 'English Language Arts 6 Advanced',
    code: '1601ADV',
    grade: 6,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'An open-enrollment course with no prerequisites that prepares students for the rigor of middle and high school advanced courses. Greater depth and complexity than standard ELA 6, with reading inside and outside of class. Students are expected to commit to enriched curriculum requirements.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Open enrollment — no prerequisites required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'ela-7',
    name: 'English Language Arts 7',
    code: '1701',
    grade: 7,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Seventh grade ELA continues the refinement of literacy skills with increasingly complex texts and writing tasks across genres.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'ela-7-advanced',
    name: 'English Language Arts 7 Advanced',
    code: '1701ADV',
    grade: 7,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Open-enrollment advanced ELA preparing students for high school advanced coursework. Greater depth, complexity, and reading volume than standard ELA 7.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Open enrollment — no prerequisites required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'ela-8',
    name: 'English Language Arts 8',
    code: '1801',
    grade: 8,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Eighth grade ELA refines and masters literacy skills in preparation for high school. Students read across genres and write for specific audiences and purposes.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'ela-8-advanced',
    name: 'English Language Arts 8 Advanced',
    code: '1801ADV',
    grade: 8,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Open-enrollment advanced ELA preparing students for high school advanced coursework. Greater depth, complexity, and reading volume than standard ELA 8.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Open enrollment — no prerequisites required.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // MATHEMATICS
  // =========================================================================
  {
    id: 'math-6',
    name: 'Math 6',
    code: '2601',
    grade: 6,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Mathematical operations with integers, fractions, and decimals; rational number conversions; algebraic relationships and equations; proportional relationships; geometric formulas; statistics; and personal financial literacy.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'math-6-7',
    name: 'Math 6/7 (Advanced Pathway)',
    code: '2613',
    grade: 6,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Combines content from both 6th and 7th grade math TEKS. Increased rigor for students who have shown a need for enrichment and acceleration.',
    prerequisites: [
      'Successful completion of Math 5/6 OR qualification for Math 6/7 (qualification info communicated to all 5th grade math students).',
    ],
    fieldTrips: null,
    notes: 'LISD Math Pathway 2.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'math-7',
    name: 'Math 7',
    code: '2701',
    grade: 7,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Operations with positive and negative rational numbers; multi-step equations and inequalities; 2D and 3D geometry; proportionality; probability; statistical analysis; and personal financial literacy.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'math-7-8',
    name: 'Math 7/8 (Advanced Pathway)',
    code: '2713',
    grade: 7,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Combines content from 7th and 8th grade math TEKS in preparation for Algebra I in grade 8. Increased rigor for students who excel in mathematics.',
    prerequisites: [
      'Successful completion of Math 6/7 OR qualification for Math 7/8.',
    ],
    fieldTrips: null,
    notes: 'LISD Math Pathway 2.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'math-8',
    name: 'Math 8',
    code: '2801',
    grade: 8,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Rational and irrational numbers; equations and inequalities with variables on both sides; surface area and volume; linear relationships; transformational geometry; statistical procedures; and personal financial literacy.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'algebra-1-advanced',
    name: 'Algebra I Advanced',
    code: '2811',
    grade: 8,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: true,
    description:
      'Use of symbols, functions, and equations to study relationships among quantities. Students model situations with concrete, numerical, algorithmic, and graphical representations. Includes preparation strategies for high school AP coursework.',
    prerequisites: ['Grade 8 math or equivalent HS Credit'],
    fieldTrips: null,
    notes:
      'Earns one high school math credit. Excluded from high school GPA. Required STAAR end-of-course exam.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'geometry-advanced',
    name: 'Geometry Advanced',
    code: '2821ADV',
    grade: 8,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: true,
    description:
      'Study of shapes and figures, spatial reasoning, mathematical systems, logical reasoning, congruence, similarity, and applied geometric problem-solving.',
    prerequisites: ['Algebra I or equivalent HS Credit'],
    fieldTrips: null,
    notes:
      'May not be offered at all middle school campuses. Transportation to a campus where the course is offered is not guaranteed. Earns one HS math credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
    verificationNeeded: true,
    verificationNote:
      'Course catalog notes this may not be offered at every campus. Confirm CRMS availability with counselor.',
  },

  // =========================================================================
  // SCIENCE
  // =========================================================================
  {
    id: 'science-6',
    name: 'Science 6',
    code: '3601',
    grade: 6,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Phenomena-based science: light and matter, thermal energy, weather and climate, water cycling, plate tectonics and rock cycling, natural hazards, and cells and systems. Emphasis on asking questions, designing investigations, and developing scientific thinking.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'science-7',
    name: 'Science 7',
    code: '3701',
    grade: 7,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Phenomena-based science: chemical reactions and matter, chemical reactions and energy, metabolic reactions, matter cycling and photosynthesis, ecosystem dynamics, and Earth\'s resources and human impact.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'science-8',
    name: 'Science 8',
    code: '3801',
    grade: 8,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Phenomena-based science: contact forces, sound waves, forces at a distance, Earth in space, genetics, and natural selection and common ancestry.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // SOCIAL STUDIES
  // =========================================================================
  {
    id: 'social-studies-6',
    name: 'Social Studies 6',
    code: '4601',
    grade: 6,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Contemporary world cultures: people, places, and regions. Concepts of history, geography, government, economics, culture, citizenship, and science and technology applied to contemporary societies.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'social-studies-7',
    name: 'Social Studies 7 (Texas History)',
    code: '4701',
    grade: 7,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'Texas history from early times to the present. Regions of Texas, local and state government, cultural background, and the impact of scientific and technological innovations on modern life.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'social-studies-8',
    name: 'Social Studies 8 (U.S. History to 1877)',
    code: '4801',
    grade: 8,
    category: 'core',
    required: true,
    advanced: false,
    hsCredit: false,
    description:
      'U.S. history from the early colonial period through Reconstruction. Political, economic, and social events of the 17th, 18th, and 19th centuries.',
    prerequisites: [],
    fieldTrips: null,
    notes: null,
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'social-studies-8-advanced',
    name: 'Social Studies 8 Advanced',
    code: '4801ADV',
    grade: 8,
    category: 'core',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Open-enrollment advanced 8th grade social studies emphasizing critical thinking — analysis, evaluation, and synthesis — in preparation for high school AP coursework. Additional focus on inquiry, document interpretation, and data analysis.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Open enrollment — no prerequisites required.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // LANGUAGES OTHER THAN ENGLISH (LOTE) — 8th grade only at MS level
  // =========================================================================
  {
    id: 'spanish-1',
    name: 'Spanish I',
    code: '5801',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Introduces basic Spanish language skills: viewing, listening, reading, speaking, and writing. Vocabulary and grammar for everyday communication, with cultural exposure through videos, presentations, and projects.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Earns one HS LOTE credit; excluded from HS GPA. Active oral participation expected.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'spanish-4-ap',
    name: 'Spanish IV: Language and Culture AP',
    code: '5802',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: true,
    hsCredit: true,
    description:
      'AP-level Spanish emphasizing real-life communication across interpersonal, interpretive, and presentational modes. Develops awareness of cultural products, practices, and perspectives. Course is taught almost exclusively in Spanish and prepares students for the AP Exam.',
    prerequisites: ['Spanish III or test placement'],
    fieldTrips: null,
    notes: 'Earns one HS LOTE credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'french-1',
    name: 'French I',
    code: '5803',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Introduces basic French language skills with emphasis on oral communication. Vocabulary and grammar for everyday situations, with cultural exposure through videos, presentations, and projects.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Earns one HS LOTE credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // FINE ARTS — Visual Arts
  // =========================================================================
  {
    id: 'art-ms-1',
    name: 'Art MS 1',
    code: '6602',
    grade: 6,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Introductory art course for students taking middle school art for the first time. No previous experience needed. Students develop artistic skills working in a variety of media.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Semester (6600) or year-long (6602). Satisfies fine arts requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'art-ms-2',
    name: 'Art MS 2',
    code: '6702',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Second-year middle school art. Develops new skills and techniques in a variety of art media while learning the elements and principles of art.',
    prerequisites: ['Art MS 1'],
    fieldTrips: null,
    notes: 'Available grades 7-8. Semester (6700) or year-long (6702).',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'art-ms-2-advanced',
    name: 'Art MS 2 Advanced',
    code: '6703',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Advanced art for students admitted by teacher recommendation. Develops advanced techniques in a variety of media plus art history, modern visual art media, and portfolio preparation for high school.',
    prerequisites: ['Art MS 1', 'Teacher recommendation'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Full-year commitment. Portfolio may be required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'art-ms-3',
    name: 'Art MS 3',
    code: '6802',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Third-year middle school art. Continued skill development across media, with focus on perception, creative expression, and art history.',
    prerequisites: ['Art MS 2'],
    fieldTrips: null,
    notes: 'Semester (6800) or year-long (6802).',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'art-ms-3-advanced',
    name: 'Art MS 3 Advanced',
    code: '6803',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Advanced 8th grade art. Advanced techniques, art history, modern visual art media, and portfolio preparation for high school art.',
    prerequisites: ['Art MS 2 or Art MS 2 Advanced', 'Teacher recommendation'],
    fieldTrips: null,
    notes: 'Full-year commitment.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'art-1-hs',
    name: 'Art I (HS Credit)',
    code: '6101MS',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Drawing, painting, and sculpting media while developing the elements and principles of art. Earns high school fine arts credit.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Earns one HS fine arts credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // FINE ARTS — Band
  // =========================================================================
  {
    id: 'band-ms-1',
    name: 'Band MS 1',
    code: '6610',
    grade: 6,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Full-year elective for students taking band for the first time. No previous musical experience needed. Instrument placement based on physical characteristics, student preference, director recommendation, and availability.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Some outside-of-class time required for concerts. Concert participation is required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'band-ms-2',
    name: 'Band MS 2',
    code: '6710',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Second-year band students placed by audition into ability-based bands. Includes weekly section rehearsals before or after school, group and individual contests, and performances throughout the year.',
    prerequisites: ['Band MS 1'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Section rehearsals before/after school required. Concert and contest participation required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'band-ms-3',
    name: 'Band MS 3',
    code: '6810',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Third-year band. Audition-based placement, weekly section rehearsals, concerts, and contests.',
    prerequisites: ['Band MS 2'],
    fieldTrips: null,
    notes:
      'Section rehearsals before/after school required. Concert and contest participation required.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // FINE ARTS — Choir
  // =========================================================================
  {
    id: 'choir-ms-1',
    name: 'Choir MS 1',
    code: '6622',
    grade: 6,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Full-year elective for students taking choir for the first time. Develops the singing voice and music-reading skills. Vocabulary, sight-reading, and notation. Voice-part placement determined by director.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Voice parts: Cambiata (6620), Treble (6622), Mixed (6624), Select (6625). Concert participation required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'choir-ms-2',
    name: 'Choir MS 2',
    code: '6722',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Second-year choir. Continued development of choral voice with opportunities for solo work. Includes All-Region, UIL, Solo and Ensemble contests, and various festivals.',
    prerequisites: ['Choir MS 1'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Full-year commitment. Concert participation required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'choir-ms-3',
    name: 'Choir MS 3',
    code: '6822',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Third-year choir. Advanced choral work with solo opportunities, contests, and performances.',
    prerequisites: ['Choir MS 2'],
    fieldTrips: null,
    notes: 'Full-year commitment. Concert participation required.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // FINE ARTS — Dance (also satisfies PE requirement)
  // =========================================================================
  {
    id: 'dance-ms-1',
    name: 'Dance MS 1',
    code: '6631',
    grade: 6,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'First-year middle school dance. Introduces dance forms and basic choreography through movement and performance. May be taken alongside Lifetime Fitness.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Semester (6630) or year-long (6631). Satisfies fine arts AND physical activity requirement. NOT a prerequisite for Dance Team.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'dance-ms-2',
    name: 'Dance MS 2',
    code: '6731',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Second-year dance. Full-year course covering ballet, modern, jazz, lyrical, hip hop, choreography, and production. Includes terminology, history, and performance opportunities.',
    prerequisites: ['Dance MS 1'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Satisfies fine arts AND physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'dance-ms-3',
    name: 'Dance MS 3',
    code: '6831',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Third-year dance. Advanced technique and choreography across multiple styles, with public performance opportunities.',
    prerequisites: ['Dance MS 2'],
    fieldTrips: null,
    notes: 'Satisfies fine arts AND physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'dance-team-ms-1',
    name: 'Dance Team MS 1',
    code: '6635',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Full-year course for 7th and 8th grade students. Members perform routines at football games, pep rallies, and other school functions. Open enrollment via clinic attendance and director recommendation.',
    prerequisites: ['Clinic attendance', 'Director recommendation'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Daily before- or after-school practices required. Satisfies fine arts AND physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'dance-team-ms-2',
    name: 'Dance Team MS 2',
    code: '6735',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Second-year dance team. Continued performances at school functions with daily practices.',
    prerequisites: ['Dance Team MS 1', 'Clinic attendance', 'Director recommendation'],
    fieldTrips: null,
    notes:
      'Daily before- or after-school practices required. Satisfies fine arts AND physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // FINE ARTS — Theatre
  // =========================================================================
  {
    id: 'theatre-ms-1',
    name: 'Theatre MS 1',
    code: '6642',
    grade: 6,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Introductory theatre course. No previous experience needed. Develops performance skills through in-class and outside-of-class performances; improves public speaking.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Semester (6640) or year-long (6642). Some outside-of-class time required for performances.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'theatre-ms-2',
    name: 'Theatre Arts MS 2',
    code: '6742',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Second-year theatre. Builds on fundamentals with deeper drama and theatre arts curriculum, technical projects, acting opportunities, and possible field trips.',
    prerequisites: ['Theatre Arts MS 1'],
    fieldTrips: 'Possible field trips and high school audition prep.',
    notes: 'Available grades 7-8. Semester (6740) or year-long (6742).',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'theatre-ms-2-advanced',
    name: 'Theatre Arts MS 2 Advanced',
    code: '6743',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Advanced theatre by application only. Advanced acting techniques, production and technology skills, theatre history, and professional audition preparation.',
    prerequisites: ['Theatre Arts MS 1', 'Application'],
    fieldTrips: null,
    notes: 'Available grades 7-8. Full-year commitment.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'theatre-ms-3',
    name: 'Theatre Arts MS 3',
    code: '6842',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Third-year theatre. Advanced curriculum, technical projects, acting opportunities, possible field trips, and high school audition preparation.',
    prerequisites: ['Theatre Arts MS 2'],
    fieldTrips: 'Possible field trips and high school audition prep.',
    notes: 'Semester (6840) or year-long (6842).',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'theatre-ms-3-advanced',
    name: 'Theatre Arts MS 3 Advanced',
    code: '6843',
    grade: 8,
    category: 'fine-arts',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Advanced 8th grade theatre by application only. Advanced acting, production and technology, theatre history, modern theatrical media, and professional audition prep.',
    prerequisites: ['Theatre Arts MS 2 or Theatre Arts MS 2 Advanced', 'Application'],
    fieldTrips: null,
    notes: 'Full-year commitment.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'tech-theatre',
    name: 'Introduction to Technical Theatre',
    code: '6744',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Beginning stage craft: scenic construction, design concepts, theatrical lighting, sound technologies, and stage management. Hands-on learning through projects and onstage productions.',
    prerequisites: ['Theatre Arts MS 1 or MS 2'],
    fieldTrips: null,
    notes: 'Available grades 7-8.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'musical-theatre',
    name: 'Musical Theatre',
    code: '6745',
    grade: 7,
    category: 'fine-arts',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Performance-focused exploration of musical theatre: showmanship, singing, acting, and dance combined. Rotation between dance, singing, and acting builds the skills to perform on stage.',
    prerequisites: ['Concurrently enrolled or successful completion of Choir or Theatre'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. May require rehearsals and performances outside the school day.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // PHYSICAL EDUCATION
  // =========================================================================
  {
    id: 'lifetime-fitness',
    name: 'Lifetime Fitness (PE)',
    code: '7601',
    grade: 6,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Teaches the values of a healthy lifestyle through team and individual sports plus health topics. Fitness assessments help students take ownership of their fitness levels. Builds goal-setting and sportsmanship.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Students supply their own workout clothes and shoes. Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'pre-athletics',
    name: 'Pre-Athletics',
    code: '7605',
    grade: 6,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'For 6th graders considering 7th grade athletics. Meets PE requirements and adds advanced curriculum and training tied to UIL sports (cross country, football, volleyball, basketball, soccer, track and field).',
    prerequisites: [],
    fieldTrips: null,
    notes:
      '6th grade only. Semester (7609/7610) or year-long (7605/7606). Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'ocpe',
    name: 'Off-Campus PE (OCPE)',
    code: '7607',
    grade: 6,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Earns PE credit through approved off-campus programs. Category 1 (Olympic-level, 15+ hrs/week, dismissal eligible) or Category 2 (high-quality private/commercial programs, 5+ hrs/week, no school dismissal).',
    prerequisites: [
      'Approved program',
      'Application by deadline (last day of school)',
      'Full-year commitment',
    ],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Fitnessgram required while enrolled. Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'boys-athletics',
    name: "Boys' Athletics",
    code: '7721',
    grade: 7,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      "Year-long commitment offering cross-country, football, basketball, track & field, and soccer. Students choosing this course are signing up to play football. Students who don't play football are enrolled in Lifetime Fitness instead.",
    prerequisites: ['Athletic physical', 'Athletic paperwork'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Must pass all classes each grading period to remain eligible. Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'girls-athletics',
    name: "Girls' Athletics",
    code: '7722',
    grade: 7,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      "Year-long commitment offering cross-country, volleyball, basketball, track & field, and soccer. 7th graders need coaching staff approval to continue as 8th graders.",
    prerequisites: [
      'Meeting with coaching staff',
      'Athletic physical',
      'Athletic paperwork',
    ],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Must pass all classes each grading period to remain eligible. Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'cheerleading',
    name: 'Cheerleading',
    code: '7711',
    grade: 7,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Open-enrollment activity for 7th and 8th graders. Cheerleaders perform at home football, volleyball, and basketball games plus pep rallies. Daily before- or after-school practices.',
    prerequisites: ['Completion of evaluation steps', 'Coach recommendation'],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Daily practices required. Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'color-guard',
    name: 'Color Guard',
    code: '7703',
    grade: 7,
    category: 'pe',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Teaches flag, rifle, and dance skills. Performs at pep rallies and competitions.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8. Satisfies physical activity requirement.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // ELECTIVES — Business / Marketing / Finance
  // =========================================================================
  {
    id: 'mxincedu',
    name: 'Entrepreneurship (mxINCedu)',
    code: '8720',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Hands-on entrepreneurship: students work in teams to identify a problem, develop a product/solution, pitch the idea, sell or test the product, evaluate performance, and iterate. Culminates in a pop-up or e-commerce marketplace experience. Class size limited.',
    prerequisites: [],
    fieldTrips: 'Pop-up marketplace and community engagement opportunities.',
    notes:
      'Available grades 7-8. May only be taken once. Limited class size.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'entrepreneurship-1',
    name: 'Entrepreneurship I (HS Credit)',
    code: '8270MS',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Uses the Uncharted Learning mxINCedu curriculum. Introduces business, entrepreneurship, and leadership through real-world challenges and project-based learning. Combines business concepts with career skills (financial management, personal branding, problem-solving). Teams identify a problem, build a product, pitch and sell, evaluate, iterate. Culminates in a pop-up or e-commerce marketplace.',
    prerequisites: [],
    fieldTrips: 'Pop-up marketplace and community engagement opportunities.',
    notes:
      'Available grades 7-8. Earns one HS elective credit toward 21 different HS Programs of Study; excluded from HS GPA. May only be taken once.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // ELECTIVES — English / Speech / Journalism / Creative Writing
  // =========================================================================
  {
    id: 'exploring-speech-debate',
    name: 'Exploring Speech/Debate',
    code: '1902',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Introduction to effective communication: expressing feelings, social traditions, informing, persuading, creative imagination. Practice in verbal and nonverbal communication, listening, and approaching controversial issues through argumentation and debate.',
    prerequisites: [],
    fieldTrips: null,
    notes: '6th grade. Semester course.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'speech-debate',
    name: 'Speech/Debate',
    code: '1903',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Develops effective communication skills for academic, social, civic, and professional roles. Apply oral language, nonverbal communication, and listening across contexts. Practice argumentation and debate on controversial issues.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'debate-1',
    name: 'Debate I (HS Credit)',
    code: '1511MS',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Various formats of high school competitive debate. Students prepare and present opposing-side speeches on resolutions. Develops refutation, burden of proof, and evaluation skills. UIL and Texas Forensic Association competition required.',
    prerequisites: [],
    fieldTrips: 'UIL and Texas Forensic Association competitions.',
    notes: 'Earns one HS elective credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'intro-creative-writing',
    name: 'Introduction to Creative Writing',
    code: '1906',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Introduces creative writing styles and forms: short story basics, poetry forms, comics, commercials, one-scene plays, flash films, and more. Students read mentor texts and produce a portfolio of their finest work.',
    prerequisites: [],
    fieldTrips: null,
    notes: '6th grade. Semester course.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'creative-writing',
    name: 'Creative Writing',
    code: '1907',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Continues from Introduction to Creative Writing. Realistic fiction, horror, dystopian, science fiction, fantasy, and poetry. Projects include a horror story podcast, an original play (performed by advanced theatre), and a short film.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'journalism-news',
    name: 'Journalism: News Media',
    code: '8773',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Create, manage, and produce school news media in print and digital formats. Develops journalistic writing, interviewing, research, photography, layout, design, and editing.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8. Student enrollment form may be required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'journalism-yearbook',
    name: 'Journalism: Yearbook',
    code: '8770',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Use software programs to publish the school yearbook. Develops photography, marketing, leadership, and journalistic writing skills. Limited seats; requires strong communication skills and reliability.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8. Student enrollment form may be required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'journalism-hs',
    name: 'Journalism (HS Credit)',
    code: '1531MS',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Fundamentals of journalism: news stories, features, editorials, sports, captions. Photography, advertising, layout, design, style, typography. Examines roles of radio, TV, and film and produces original work.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Earns one HS elective credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // ELECTIVES — JROTC (Henry MS only — included for visibility)
  // =========================================================================
  {
    id: 'jrotc-navy-1',
    name: 'JROTC Navy I',
    code: '8537MS',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Naval Science I: citizenship, leadership, scholarship, and the heritage and tradition of the United States with focus on sea power. Includes classroom instruction, physical fitness, military drill, uniform wearing, and military customs.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'NOT OFFERED AT CRMS — only at Henry Middle School per military regulations. Earns one HS elective credit and one PE substitution credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
    verificationNeeded: true,
    verificationNote: 'Only offered at Henry MS per military regulations. Confirm not available at CRMS.',
  },

  // =========================================================================
  // ELECTIVES — Leadership
  // =========================================================================
  {
    id: 'exploring-leadership',
    name: 'Exploring Foundations of Leadership',
    code: '8650',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Introductory leadership course. Personal responsibility, role play, creative activities, and discussion. Teaches that leadership is about being your personal best and supporting others.',
    prerequisites: [],
    fieldTrips: null,
    notes: '6th grade. Semester course.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'leadership',
    name: 'Leadership',
    code: '8849',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Leadership skills in an interactive atmosphere. Personal responsibility, communication, public speaking, and direct involvement in community service projects.',
    prerequisites: [],
    fieldTrips: 'Community service projects.',
    notes:
      'Available grades 7-8. Semester (8850) or year-long (8849). May only be taken once.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'advanced-leadership',
    name: 'Advanced Leadership',
    code: '8847',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'Applies leadership knowledge from Exploring Foundations of Leadership or Leadership. Decision-making, problem-solving, communication, leadership roles, human relations, social intelligence, and civic responsibility.',
    prerequisites: ['Exploring Foundations of Leadership OR Leadership'],
    fieldTrips: null,
    notes:
      'Semester (8846) or year-long (8847). Student enrollment form may be required.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'leadworthy',
    name: 'Leadership: Leadworthy The Course (HS Credit)',
    code: '8848',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Develops personal responsibility, leadership, and professional skills through social-emotional learning. Personal image, healthy relationships, consequential thinking, awareness of social media and peer pressure, public speaking, vision and goals, and community service.',
    prerequisites: [],
    fieldTrips: 'Community service.',
    notes: 'Earns 0.5 HS elective credit; excluded from HS GPA. Semester course.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // ELECTIVES — STEM
  // =========================================================================
  {
    id: 'exploring-stem',
    name: 'Exploring STEM',
    code: '8606',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Survey course: science, technology, engineering, mathematics, and design used together to address societal issues. Topics include computer science, 3D modeling, robotics, chemical engineering, electrical engineering, biomedical engineering, mechanical engineering, civil engineering, and aerospace science.',
    prerequisites: [],
    fieldTrips: null,
    notes: '6th grade. Course length and curriculum may vary by campus.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'applied-stem',
    name: 'Applied STEM',
    code: '8602',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Project-based STEM with focus on real-world applications. Career connections to engineering, design, medicine, research, urban planning. Possible projects: space station for life support, functional model of a city, app development.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Semester course. Intended for students who have NOT taken Exploring STEM in 6th grade.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'gateway-cs-app-creator',
    name: 'Gateway: Computer Science / App Creator',
    code: '8712',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Computer Science Innovators and Makers: programming projects with physical products using Microsoft MakeCode and micro:bits. App Creators: computational thinking through mobile app development; teams build mobile solutions to authentic problems.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'fundamentals-cs',
    name: 'Fundamentals of Computer Science (HS Credit)',
    code: '8449MS',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Programming languages and concepts preparing students for HS computer science. Build websites, apps, animations, games, and physical computing systems. PLTW Gateway modules cover problem solving, critical thinking, and physical computing.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Earns one HS elective credit toward Computer Science Program of Study within the STEM Endorsement; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'gateway-design-robotics',
    name: 'Gateway: Design and Modeling / Automation Robotics',
    code: '8713',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Design and Modeling: engineering design process, 3D design with TinkerCAD. Automation and Robotics: history of automation, mechanical systems, energy transfer, machine automation, and computer control. Use VEX Robotics platform.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'principles-engineering',
    name: 'Principles of Applied Engineering (HS Credit)',
    code: '8313MS',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Real-world engineering applications. Mechanical, electrical, civil, and chemical engineering careers and industry standards. Collaborative real-world problem-solving with research and design presentations.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 7-8. Earns one HS elective credit toward Engineering Foundations Program of Study within the STEM Endorsement; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'gateway-medical-detectives',
    name: 'Gateway: Medical Detectives',
    code: '8714',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Step into the role of a medical detective: collect and analyze medical data to diagnose conditions. Hands-on labs include vital signs, the nervous system, disease outbreaks, forensic science, and brain and eye dissection.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8. Semester or year-long.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // ELECTIVES — Technology and Multimedia
  // =========================================================================
  {
    id: 'tech-applications',
    name: 'Technology Applications',
    code: '8704',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Creativity, communication, research, critical thinking, problem solving, decision-making, digital citizenship, and technology operations. Hands-on experience with keyboarding, slide presentations, spreadsheets, website anatomy, email, and file management.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 6-8. May only be taken once.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'multimedia-productions',
    name: 'Multimedia Productions',
    code: '1904',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Digital media production: hardware and software tools for professional-level media. Studio and set lighting, camera functions, art and design elements, shot composition, script writing. Second-year students manage a production studio creating content for campus clients.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Available grades 7-8.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'principles-av',
    name: 'Principles of Arts, A/V, and Communications (HS Credit)',
    code: '8464MS',
    grade: 8,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: true,
    description:
      'Career exploration in arts, A/V technology, and communications. Topics include audio/video production, graphic design and illustration, and animation.',
    prerequisites: [],
    fieldTrips: null,
    notes: 'Earns one HS elective credit; excluded from HS GPA.',
    source: SOURCES.courseCatalog2627,
  },

  // =========================================================================
  // ELECTIVES — Additional
  // =========================================================================
  {
    id: 'avid',
    name: 'AVID',
    code: '8669',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Advancement Via Individual Determination — year-long college and career readiness elective. Focus on writing, inquiry, collaboration, organization, and reading (WICOR) plus tutorial support, focused note-taking, time management, and research strategies.',
    prerequisites: [
      'Student application',
      'Oral and written interview process',
    ],
    fieldTrips: null,
    notes:
      'Available grades 6-8 (codes 8669/8769/8869). Continues all four years of high school.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'bridges',
    name: 'Bridges to Academic Success',
    code: '8668',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Semester course for 6th graders focused on academic and personal skills for middle school success. Time management, organizational skills, motivation, and team-building. Designed to ease the elementary-to-middle-school transition.',
    prerequisites: [],
    fieldTrips: null,
    notes: '6th grade only. Semester course.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'math-la-extension',
    name: 'Math & Language Arts Extension',
    code: null,
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Builds academic behaviors, key cognitive strategies, and deeper understanding/application of key content knowledge. Placement is determined by campus.',
    prerequisites: ['Campus placement'],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Course code not specified in catalog.',
    source: SOURCES.courseCatalog2627,
    verificationNeeded: true,
    verificationNote:
      'Course code not listed in published catalog. Confirm with CRMS counselor.',
  },
  {
    id: 'quest-studies',
    name: 'QUEST Studies (G/T)',
    code: '8651',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: true,
    hsCredit: false,
    description:
      'For students identified as gifted and talented. Interdisciplinary studies across language arts, math, social studies, and science using project-based learning, creative problem solving, and critical thinking. Develops self-regulation, task persistence, grit, strategic risk-taking, collaboration, resilience, and optimism.',
    prerequisites: ['G/T identification per LISD criteria'],
    fieldTrips: null,
    notes:
      'Available grades 6-8 (codes 8651/8751/8851). All G/T-identified students strongly encouraged to take in 6th grade.',
    source: SOURCES.courseCatalog2627,
  },
  {
    id: 'student-aide',
    name: 'Student Aide',
    code: '8771',
    grade: 7,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Assist with various tasks while learning basic skills. Enrollment criteria and placement determined by campus.',
    prerequisites: ['Campus approval'],
    fieldTrips: null,
    notes:
      'Semester (8760) or year-long (8771). Grade availability depends on campus.',
    source: SOURCES.courseCatalog2627,
    verificationNeeded: true,
    verificationNote: 'Grade availability varies by campus. Confirm with CRMS.',
  },
  {
    id: 'elective-wheel',
    name: 'Elective Wheel',
    code: '8621',
    grade: 6,
    category: 'elective',
    required: false,
    advanced: false,
    hsCredit: false,
    description:
      'Rotation of shortened courses across the year, allowing students to explore multiple elective offerings. Course offerings may vary by campus.',
    prerequisites: [],
    fieldTrips: null,
    notes:
      'Available grades 6-8. Course offerings vary by campus — may not be offered at CRMS.',
    source: SOURCES.courseCatalog2627,
    verificationNeeded: true,
    verificationNote:
      'Catalog notes "some campuses may offer" this. Confirm CRMS availability.',
  },
];

export default courses;
